import useTranslations from "@/shared/lang/useTranslations";

import type { TicketNotice } from "../components/swap/QuoteTicket";
import { CONCEPT_MAX_LENGTH, SAVED_WALLET_ALIAS_MAX_LENGTH } from "../constants/cryptoSwaps.constants";
import { cryptoSwapErrorText, isRangeRejection } from "../utils/cryptoSwapErrors";
import { CRYPTO_SWAP_BLOCK_MESSAGE_KEYS } from "../utils/cryptoSwapStatus";
import { useAmountIssueText } from "./useAmountIssueText";
import type { UseCryptoSwapFormResult } from "./useCryptoSwapForm";
import { useCryptoSwapLabels } from "./useCryptoSwapLabels";

/** The review button says exactly what the offending field says; the ticket explains what a field cannot. */
export function useCryptoSwapFormMessages(form: UseCryptoSwapFormResult) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const issueText = useAmountIssueText(form.pair);

  const quoteError = form.quoteError;
  const quoteErrorText = quoteError ? cryptoSwapErrorText(quoteError, t) : null;
  const network = labels.networkShort(form.network);

  const destinationText =
    form.destinationIssue === "empty"
      ? t("destination_address_empty_hint")
      : form.destinationIssue === "invalid"
        ? t("destination_address_invalid", { network })
        : form.destinationIssue === "unsupported_network"
          ? t("destination_network_unsupported")
          : form.destinationIssue === "concept"
            ? t("concept_too_long", { max: CONCEPT_MAX_LENGTH })
            : form.destinationIssue === "save_holder"
              ? t("saved_wallet_holder_required")
              : form.destinationIssue === "save_alias"
                ? t("saved_wallet_alias_too_long", { max: SAVED_WALLET_ALIAS_MAX_LENGTH })
                : null;

  const blockMessage = form.reviewIssue
    ? issueText(form.reviewIssue)
    : form.block === "destination" && destinationText
      ? destinationText
      : form.block
        ? t(CRYPTO_SWAP_BLOCK_MESSAGE_KEYS[form.block])
        : null;

  const notice: TicketNotice | null =
    form.block === "expired"
      ? { kind: "expired" }
      : form.isPricing
        ? null
        : form.isManualMode
          ? { kind: "manual" }
          : quoteError && quoteErrorText && !isRangeRejection(quoteError) && !form.quote
            ? { kind: "failed", message: quoteErrorText, retryable: quoteError.retryable }
            : null;

  return { blockMessage, notice, quoteErrorText };
}
